// Parses the reference tables in src/content/tables/*.md (indicators and actions) for ReferenceTableDialog.
// Structure: front matter, "## Group", "### Item", "#### Field" (see the editing guide at the top of each file).

const ALLOWED_TAGS = /&lt;(\/?)(sub|sup|br|b|strong|i|em)\s*\/?&gt;/gi;

const escapeHtml = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// **bold**, [text](url), bare URLs and a few inline HTML tags (<sub>, <br>...)
export function inlineToHtml(text) {
  const links = [];
  const stash = (html) => `\u0000${links.push(html) - 1}\u0000`;
  let html = escapeHtml(text).replace(ALLOWED_TAGS, "<$1$2>");
  html = html.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, url) =>
    stash(`<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`));
  html = html.replace(/https?:\/\/[^\s<]+[^\s<.,;)]/g, (url) =>
    stash(`<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`));
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  return html.replace(/\u0000(\d+)\u0000/g, (_, i) => links[Number(i)]);
}

const isTableLine = (line) => /^\s*\|.*\|\s*$/.test(line);
const tableCells = (line) => line.trim().replace(/^\||\|$/g, "").split("|").map((cell) => cell.trim());

// Paragraphs, "- " lists and pipe tables -> HTML
export function blocksToHtml(text) {
  const out = [];
  let list = null;
  let para = [];
  const flushPara = () => { if (para.length) out.push(`<p>${inlineToHtml(para.join(" "))}</p>`); para = []; };
  const flushList = () => { if (list) out.push(`<ul>${list.map((item) => `<li>${inlineToHtml(item)}</li>`).join("")}</ul>`); list = null; };
  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (isTableLine(line)) {
      flushPara(); flushList();
      const rows = [];
      while (i < lines.length && isTableLine(lines[i])) rows.push(tableCells(lines[i++]));
      i--;
      const [head, , ...body] = rows;
      out.push(`<table><thead><tr>${head.map((c) => `<th>${inlineToHtml(c)}</th>`).join("")}</tr></thead><tbody>${
        body.map((r) => `<tr>${r.map((c) => `<td>${inlineToHtml(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>`);
    } else if (/^\s*-\s+/.test(line)) {
      flushPara();
      (list = list || []).push(line.replace(/^\s*-\s+/, ""));
    } else if (!line.trim()) {
      flushPara(); flushList();
    } else if (list && /^\s{2,}\S/.test(line)) {
      list[list.length - 1] += " " + line.trim(); // continuation of a list item
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara(); flushList();
  return out.join("");
}

function parseFrontMatter(text) {
  const match = text.match(/^---\n([\s\S]*?)\n---\n/);
  const meta = {};
  if (!match) return { meta, body: text };
  match[1].split("\n").forEach((line) => {
    const kv = line.match(/^(\w+):\s*(.*)$/);
    if (kv && !kv[2].startsWith("[")) meta[kv[1]] = kv[2].trim();
  });
  return { meta, body: text.slice(match[0].length) };
}

// Document -> { meta, groups: [{ name, items: [{ name, note, fields: { "Field name": "raw markdown" } }] }] }
export function parseTableDoc(text) {
  const { meta, body } = parseFrontMatter(text.replace(/\r\n/g, "\n"));
  const clean = body
    .replace(/```\{=html\}\n?([\s\S]*?)```\n?/g, "$1") // raw-HTML fences some editors add around comments
    .replace(/<!--[\s\S]*?-->/g, "");
  const groups = [];
  let group = null;
  let item = null;
  let field = null;
  clean.split("\n").forEach((line) => {
    const heading = line.match(/^(#{2,4})\s+(.*)$/);
    if (heading) {
      const [, hashes, title] = heading;
      if (hashes === "##") { group = { name: title.trim(), items: [] }; groups.push(group); item = null; field = null; }
      else if (hashes === "###" && group) { item = { name: title.trim(), note: "", fields: {} }; group.items.push(item); field = null; }
      else if (hashes === "####" && item) { field = title.trim(); item.fields[field] = ""; }
      return;
    }
    if (item && field) item.fields[field] += line + "\n";
    else if (item) item.note += line + "\n";
  });
  groups.forEach((g) => g.items.forEach((it) => {
    it.note = it.note.trim();
    Object.keys(it.fields).forEach((k) => { it.fields[k] = it.fields[k].trim(); });
  }));
  return { meta, groups };
}

// Actions: "#### Impact levels" table (or "n/a") -> [{ level, description }]
export function parseImpactLevels(raw) {
  if (!raw || /^n\/a$/i.test(raw.trim())) return [];
  return raw.split("\n").filter(isTableLine).slice(2).map(tableCells)
    .map(([level = "", description = ""]) => ({ level, description }));
}

// Actions: "#### Multiplier" as "- **Level:** text" for every level -> { Level: text }, otherwise null
export function parsePerLevelMultiplier(raw, levels) {
  if (!levels.length) return null;
  const items = raw.split("\n").filter((l) => l.trim());
  const byLevel = {};
  for (const line of items) {
    const m = line.match(/^\s*-\s+\*\*(.+?):\*\*\s*(.*)$/);
    if (!m) return null;
    byLevel[m[1].trim()] = m[2];
  }
  return levels.every((l) => byLevel[l.level] !== undefined) ? byLevel : null;
}
